/**
 * TranscriptionKeyGate — block/warn UI for audio features when the user has
 * no usable BYOK key.
 *
 * Two render shapes:
 *   - <TranscriptionKeyGate.Modal /> — full-screen blocker (used by the
 *     Consultas + Criativos upload flow before the user even picks a file).
 *   - <TranscriptionKeyGate.Inline /> — small alert/banner inside an existing
 *     surface (used in the Simulador chat composer).
 *
 * Both link to /perfil#api-keys for self-service config.
 */

import { Link } from "react-router-dom";
// Tier-4 followup carry-over: this file was missed by the lucide→phosphor
// migration. Substituted with Phosphor equivalents (Warning, Key) which match
// the visual semantics. Surgical fix to unblock build/lint on Tier 1 branch.
import { Warning as AlertTriangle, Key as KeyRound } from "@phosphor-icons/react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const COPY = {
  title: "Configure uma API key para transcrever áudio",
  body:
    "Recursos de áudio usam transcrição da OpenAI. Para continuar, cadastre uma API key da OpenAI em Perfil → API Keys.",
  cta: "Configurar API Keys",
  ctaPath: "/perfil",
} as const;

export interface TranscriptionKeyModalProps {
  open: boolean;
  onClose: () => void;
}

function TranscriptionKeyGateModal({ open, onClose }: TranscriptionKeyModalProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-amber-500" />
            {COPY.title}
          </DialogTitle>
          <DialogDescription className="pt-2 text-sm leading-relaxed">
            {COPY.body}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
          <Button asChild>
            <Link to={COPY.ctaPath} onClick={onClose}>{COPY.cta}</Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export interface TranscriptionKeyInlineProps {
  className?: string;
}

function TranscriptionKeyGateInline({ className }: TranscriptionKeyInlineProps) {
  return (
    <Alert variant="destructive" className={className}>
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>Transcrição indisponível</AlertTitle>
      <AlertDescription className="space-y-2">
        <p>{COPY.body}</p>
        <Button asChild size="sm" variant="outline">
          <Link to={COPY.ctaPath}>{COPY.cta}</Link>
        </Button>
      </AlertDescription>
    </Alert>
  );
}

export const TranscriptionKeyGate = {
  Modal: TranscriptionKeyGateModal,
  Inline: TranscriptionKeyGateInline,
};
